import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";

import type { DateRange } from "@/components/cinch/DatePicker";
import { SectionDivider } from "@/components/cinch/SectionDivider";
import { Button } from "@/components/ui/button";
import { useCan } from "@/lib/auth";
import { cn } from "@/lib/utils";

import {
  monthLabel,
  monthLabelShort,
  useKpiDashboard,
  useKpiRefresh,
  useRenewalRate,
  type KpiDashboardResponse,
  type KpiMonthRange,
} from "../api";
import type { KpiSearch } from "..";
import { ChurnCard, type ChurnData } from "../components/ChurnCard";
import { BookedNotBilledDrawer } from "../components/BookedNotBilledDrawer";
import { ChurnDrawer } from "../components/ChurnDrawer";
import {
  ConversionRateTrends,
  type ConversionRate,
} from "../components/ConversionRateTrends";
import { DealFlowSankey, type DealFlowData } from "../components/DealFlowSankey";
import { FunnelDrawer } from "../components/FunnelDrawer";
import {
  DollarRetentionCard,
  type DollarRetentionData,
} from "../components/DollarRetentionCard";
import { GrowthCard } from "../components/GrowthCard";
import type { GrowthRateData } from "../components/GrowthRateCard";
import { NewBusinessCard, type NewBusinessData } from "../components/NewBusinessCard";
import { NewBusinessDrawer } from "../components/NewBusinessDrawer";
import {
  PeriodSelector,
  type KpiPeriod,
} from "../components/PeriodSelector";
import { ProductArrTrends, type ProductArrData, type ProductTrend } from "../components/ProductArrTrends";
import { RenewalRateCard } from "../components/RenewalRateCard";
import { RetentionDrawer } from "../components/RetentionDrawer";
import { TotalArrCard, type TotalArrData } from "../components/TotalArrCard";
import { UpsellCard, type UpsellData } from "../components/UpsellCard";
import { UpsellDrawer } from "../components/UpsellDrawer";
import { fmtArr } from "../format";

const toYm = (d: Date | null): string | null =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` : null;

/** "2026-01" + "2026-05" → "Jan–May 2026" (both YYYY-MM, same year). */
function ytdRevenueLabel(start: string | null, end: string): string {
  const endName = MONTH_NAMES[Number(end.slice(5, 7)) - 1];
  const endYear = end.slice(0, 4);
  if (!start) return `${endName} ${endYear}`;
  const startName = MONTH_NAMES[Number(start.slice(5, 7)) - 1];
  if (start === end) return `${endName} ${endYear}`;
  return `${startName}–${endName} ${endYear}`;
}

export function KpiDashboardPage() {
  const [period, setPeriod] = useState<KpiPeriod>("MTD");
  const [customRange, setCustomRange] = useState<DateRange>({ start: null, end: null });
  const ymRange = useMemo<KpiMonthRange>(
    () => ({ start: toYm(customRange.start), end: toYm(customRange.end) }),
    [customRange],
  );
  const { data, error, isFetching } = useKpiDashboard(period, ymRange);
  const refresh = useKpiRefresh(period, ymRange);
  const canRefresh = useCan("kpi.write");
  const canRenewals = useCan("renewals.read");
  const renewalRate = useRenewalRate(canRenewals);
  const navigate = useNavigate();

  const { periodLabel, rangeLabel, retentionSectionLabel, partialMonthWarning } =
    useMemo(() => derivePeriodCopy(period, ymRange), [period, ymRange]);
  const mapped = useMemo(() => (data ? mapDashboard(data) : null), [data]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Company KPI Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Live revenue, pipeline, and product KPIs from Snowflake
            (BI.REVENUE_WATERFALL · BILLING_CONTRACTS) and HubSpot. Cached 4h
            server-side.
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          {data && (
            <span className="text-[11px] text-muted-foreground">
              Refreshed {new Date(data.refreshed_at).toLocaleString()}
            </span>
          )}
          {canRefresh && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              <RefreshCw
                className={cn("mr-1.5 h-3.5 w-3.5", refresh.isPending && "animate-spin")}
              />
              {refresh.isPending ? "Refreshing…" : "Refresh"}
            </Button>
          )}
        </div>
      </div>

      {error != null && (
        <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger-light/60 px-4 py-2.5 text-xs font-semibold text-danger">
          <AlertTriangle className="mt-px h-4 w-4 flex-shrink-0" />
          <span>
            KPI data unavailable: {error instanceof Error ? error.message : String(error)}
          </span>
        </div>
      )}

      <PeriodSelector
        value={period}
        onChange={setPeriod}
        rangeLabel={rangeLabel}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
      />

      {/* Top row — Total ARR (hero, 2 cols) + New + ARR Growth */}
      <div
        className={cn(
          "grid grid-cols-1 gap-3.5 md:grid-cols-2 lg:grid-cols-4",
          isFetching && !data && "animate-pulse",
        )}
      >
        <TotalArrCard
          periodLabel={mapped?.latestArrLabel ?? periodLabel}
          data={mapped?.totalArr ?? null}
          onBookedClick={() =>
            navigate({ to: "/kpi", search: (p: KpiSearch) => ({ ...p, tile: "booked" }) })
          }
        />
        <button
          type="button"
          onClick={() =>
            navigate({ to: "/kpi", search: (p: KpiSearch) => ({ ...p, tile: "new_business" }) })
          }
          className="h-full rounded-xl text-left outline-none ring-success/40 transition hover:ring-2 focus-visible:ring-2"
          aria-label="Open New business drill-down"
        >
          <NewBusinessCard periodLabel={periodLabel} data={mapped?.newBusiness ?? null} />
        </button>
        <GrowthCard
          anchorLabel={mapped ? monthLabel(mapped.anchorMonth) : "last complete month"}
          yoy={mapped?.yoy ?? null}
          mom={mapped?.mom ?? null}
        />
      </div>

      <SectionDivider label="Revenue Health" description={retentionSectionLabel} />

      <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 lg:grid-cols-4">
        <button
          type="button"
          onClick={() =>
            navigate({ to: "/kpi", search: (p: KpiSearch) => ({ ...p, tile: "retention" }) })
          }
          className="h-full rounded-xl text-left outline-none ring-purple/40 transition hover:ring-2 focus-visible:ring-2"
          aria-label="Open $ Retention drill-down"
        >
          <DollarRetentionCard
            label={`$ Retention — ${mapped ? monthLabel(mapped.anchorMonth) : "latest complete month"}`}
            data={mapped?.retention ?? null}
          />
        </button>
        <RenewalRateCard
          data={renewalRate.data ?? null}
          denied={!canRenewals || renewalRate.isError}
        />
        <button
          type="button"
          onClick={() =>
            navigate({ to: "/kpi", search: (p: KpiSearch) => ({ ...p, tile: "upsell" }) })
          }
          className="h-full rounded-xl text-left outline-none ring-warning/40 transition hover:ring-2 focus-visible:ring-2"
          aria-label="Open Upsell drill-down"
        >
          <UpsellCard periodLabel={periodLabel} data={mapped?.upsell ?? null} />
        </button>
        <button
          type="button"
          onClick={() =>
            navigate({ to: "/kpi", search: (p: KpiSearch) => ({ ...p, tile: "churn" }) })
          }
          className="h-full rounded-xl text-left outline-none ring-danger/40 transition hover:ring-2 focus-visible:ring-2"
          aria-label="Open Churn & Contraction drill-down"
        >
          <ChurnCard periodLabel={periodLabel} data={mapped?.churn ?? null} />
        </button>
      </div>

      <SectionDivider label="Pipeline Flow" description={periodLabel} />

      {partialMonthWarning && (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning-light/60 px-4 py-2.5 text-xs font-semibold text-warning">
          <AlertTriangle className="mt-px h-4 w-4 flex-shrink-0" />
          <span>{partialMonthWarning}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-4">
        <DealFlowSankey
          periodLabel={periodLabel}
          data={mapped?.funnel ?? null}
          onNodeClick={(stage) =>
            navigate({ to: "/kpi", search: (p: KpiSearch) => ({ ...p, tile: "funnel", stage }) })
          }
        />
        <ConversionRateTrends
          rates={mapped?.rates ?? null}
          source={mapped?.ratesSource}
        />
      </div>

      <SectionDivider label="Product ARR Trends" description="BI.REVENUE_WATERFALL" />

      <ProductArrTrends data={mapped?.products ?? null} />

      <RetentionDrawer period={period} range={ymRange} />
      <NewBusinessDrawer period={period} range={ymRange} periodLabel={periodLabel} />
      <FunnelDrawer period={period} range={ymRange} periodLabel={periodLabel} />
      <UpsellDrawer period={period} range={ymRange} periodLabel={periodLabel} />
      <ChurnDrawer period={period} range={ymRange} periodLabel={periodLabel} />
      <BookedNotBilledDrawer />
    </div>
  );
}

// ─── payload → component props ────────────────────────────────────────

const PRODUCT_COLORS = new Set<ProductTrend["color"]>([
  "brand", "power", "purple", "success", "warning", "danger", "teal", "orange",
]);

interface MappedDashboard {
  anchorMonth: string;
  latestArrLabel: string;
  totalArr: TotalArrData;
  yoy: GrowthRateData;
  mom: GrowthRateData;
  newBusiness: NewBusinessData;
  upsell: UpsellData;
  churn: ChurnData;
  retention: DollarRetentionData | null;
  funnel: DealFlowData;
  rates: ConversionRate[];
  ratesSource: string;
  products: ProductArrData;
}

function mapDashboard(d: KpiDashboardResponse): MappedDashboard {
  const arrLabel = d.arr.latest_is_partial
    ? `${monthLabel(d.arr.latest_month)} (billing in progress)`
    : monthLabel(d.arr.latest_month);

  const totalArr: TotalArrData = {
    current: d.arr.latest_arr,
    lastCompleteMonth: {
      label: `${monthLabel(d.arr.anchor_month)} (complete)`,
      value: d.arr.anchor_arr,
    },
    bookedNotBilled: d.arr.booked_not_billed,
    bookedNotBilledAccounts: d.arr.booked_not_billed_accounts,
    billedPlusBooked: d.arr.billed_plus_booked ?? null,
    annualizedRevenue: d.arr.annualized_revenue ?? null,
    annualizedRevenueLabel: monthLabel(d.arr.annualized_revenue_month),
    annualizedRevenue3moAvg: d.arr.annualized_revenue_3mo_avg ?? null,
    ytdRevenue: d.arr.ytd_revenue ?? null,
    ytdRevenueLabel: ytdRevenueLabel(d.arr.ytd_revenue_start_month, d.arr.ytd_revenue_end_month),
    netRevenue: d.arr.net_revenue ?? null,
    bookedNotBilledNote: d.arr.booked_not_billed_note,
    source: `BI.REVENUE_WATERFALL · subscription MRR × 12 · ${d.arr.active_customers} active customers · booked = signed contracts not yet billing`,
  };

  const yoy: GrowthRateData = {
    pct: d.growth.yoy.pct,
    deltaArr: d.growth.yoy.delta_arr,
    comparisonLabel: `vs ${monthLabel(d.growth.yoy.vs_month)}`,
    trend: d.growth.yoy.trend,
    source: "YoY % of monthly ARR · trailing 12 months",
  };
  const mom: GrowthRateData = {
    pct: d.growth.mom.pct,
    deltaArr: d.growth.mom.delta_arr,
    comparisonLabel: `vs ${monthLabel(d.growth.mom.vs_month)}`,
    trend: d.growth.mom.trend,
    source: "MoM % of monthly ARR · trailing 12 months",
  };

  const upsellUp = d.upsell.total_arr >= d.upsell.prior_total_arr;
  const upsell: UpsellData = {
    total: d.upsell.total_arr,
    comparisonLabel: `vs ${d.upsell.prior_label} (${fmtArr(d.upsell.prior_total_arr, 0)})`,
    comparisonDirection: upsellUp ? "up" : "down",
    rows: [
      {
        label: `Contract upsell delta (${d.upsell.contracts})`,
        mrr: d.upsell.contract_delta_mrr,
        arr: d.upsell.contract_delta_arr,
      },
      { label: "Billed expansion × 12", arr: d.upsell.expansion_arr },
      { label: "New logo MRR × 12", arr: d.upsell.new_logo_arr },
    ],
    source: "BILLING_CONTRACTS replacements/upsells + REVENUE_WATERFALL expansion",
  };

  const churn: ChurnData = {
    total: d.churn.total_arr,
    rows: [
      { label: "Churn MRR × 12", arr: -d.churn.churn_arr },
      { label: "Contraction MRR × 12", arr: -d.churn.contraction_arr },
    ],
    netExpansionVsChurn: d.churn.net_expansion_arr,
    source: "BI.REVENUE_WATERFALL · AMOUNT_CHURN / AMOUNT_CONTRACTED",
  };

  const nb = d.new_business;
  const newBusiness: NewBusinessData = {
    totalArr: nb.total_arr,
    totalArrPctChange: nb.total_arr_pct_change,
    priorLabel: nb.prior_label,
    newContracts: nb.new_contracts,
    newArr: nb.new_arr,
    plgContracts: nb.plg_contracts,
    plgArr: nb.plg_arr,
    poContracts: nb.po_contracts,
    poArr: nb.po_arr,
    avgDealArr: nb.avg_deal_arr,
    firstBilledArr: nb.first_billed_arr,
    firstBilledCustomers: nb.first_billed_customers,
    firstBilledArrPctChange: nb.first_billed_arr_pct_change,
  };

  const retention: DollarRetentionData | null = d.retention.available
    ? {
        nrrPct: d.retention.nrr_pct ?? null,
        gdrPct: d.retention.gdr_pct ?? null,
        baseArr: d.retention.base_arr ?? 0,
        currentArr: d.retention.current_arr ?? 0,
        retainedArr: d.retention.retained_arr ?? 0,
        baseCount: d.retention.base_count ?? 0,
        source: `Revenue waterfall · YoY current vs 12 mo prior · ${monthLabel(d.retention.anchor_month)} · entity-rolled`,
      }
    : null;

  const funnel: DealFlowData = {
    funnel: d.funnel,
    source: "HUBSPOT_DEALS · BILLING_CONTRACTS · HUBSPOT_COMPANIES",
  };

  const rates: ConversionRate[] = d.rates.map((r) => ({
    key: r.key,
    label: r.label,
    value: r.value,
    prev: r.prev,
    yoy: r.yoy,
    goodDirection: r.good_direction,
    min: r.min ?? null,
    max: r.max ?? null,
    won: r.won,
    closed: r.closed,
    stillOpen: r.still_open,
    total: r.total,
  }));
  const ratesSource =
    "Scoped to the selected period's complete months, vs prior window & prior year · " +
    "HubSpot cohorts + REVENUE_WATERFALL churn/contraction · Won→Billed pending booked→billed fix";

  const products: ProductArrData = {
    periods: d.products.months.map(monthLabelShort),
    products: d.products.items.map((p) => ({
      key: p.key,
      name: p.name,
      color: PRODUCT_COLORS.has(p.color as ProductTrend["color"])
        ? (p.color as ProductTrend["color"])
        : "brand",
      arr: p.arr,
      accounts: p.accounts,
      note: p.note ?? undefined,
    })),
  };

  return {
    anchorMonth: d.anchor_month,
    latestArrLabel: arrLabel,
    totalArr,
    yoy,
    mom,
    newBusiness,
    upsell,
    churn,
    retention,
    funnel,
    rates,
    ratesSource,
    products,
  };
}

// ─── period selector copy (presentation only) ─────────────────────────

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const DAYS_IN_MONTH = (y: number, m0: number) => new Date(y, m0 + 1, 0).getDate();

function derivePeriodCopy(period: KpiPeriod, range?: KpiMonthRange) {
  const now = new Date();
  const m = now.getMonth();
  const d = now.getDate();
  const y = now.getFullYear();
  const nowYm = `${y}-${String(m + 1).padStart(2, "0")}`;

  if (period === "CUSTOM") {
    const ready = !!(range?.start && range.end);
    const label = !ready
      ? "Custom range"
      : range.start === range.end
        ? monthLabel(range.start)
        : `${monthLabel(range.start)} – ${monthLabel(range.end)}`;
    const endsNow = ready && range.end === nowYm;
    return {
      periodLabel: label,
      rangeLabel: ready ? "Snapped to calendar months" : "Pick a start and end date",
      retentionSectionLabel: "Selected Range",
      partialMonthWarning: endsNow
        ? `${monthLabel(nowYm)} is still in progress (through ${MONTH_NAMES[m]} ${d}) — ` +
          "the range's final month reflects billing posted so far."
        : undefined,
    };
  }

  if (period === "MTD") {
    return {
      periodLabel: `${MONTH_NAMES[m]} ${y} MTD`,
      rangeLabel: `${MONTH_NAMES[m]} 1–${d}, ${y}`,
      retentionSectionLabel: "This Month",
      partialMonthWarning:
        `${MONTH_NAMES[m]} ${y} MTD is a partial month (through ${MONTH_NAMES[m]} ${d}). ` +
        `HubSpot deal creation is batch-based, so MTD funnel counts run low early in the ` +
        `month — prefer YTD for rate stability.`,
    };
  }
  if (period === "LAST_MONTH") {
    const lmY = m === 0 ? y - 1 : y;
    const lmM = m === 0 ? 11 : m - 1;
    const lastDay = DAYS_IN_MONTH(lmY, lmM);
    return {
      periodLabel: `${MONTH_NAMES[lmM]} ${lmY}`,
      rangeLabel: `${MONTH_NAMES[lmM]} 1–${lastDay}, ${lmY}`,
      retentionSectionLabel: "Last Month",
      partialMonthWarning: undefined,
    };
  }
  if (period === "QTD") {
    const qs = Math.floor(m / 3) * 3;
    return {
      periodLabel: `Q${Math.floor(m / 3) + 1} ${y} QTD`,
      rangeLabel: `${MONTH_NAMES[qs]} 1 – ${MONTH_NAMES[m]} ${d}, ${y}`,
      retentionSectionLabel: "This Quarter",
      partialMonthWarning: undefined,
    };
  }
  if (period === "LAST_QUARTER") {
    const curQ = Math.floor(m / 3);
    const lastQ = curQ === 0 ? 3 : curQ - 1;
    const lqY = curQ === 0 ? y - 1 : y;
    const lqStartM = lastQ * 3;
    const lqEndM = lqStartM + 2;
    const lqEndDay = DAYS_IN_MONTH(lqY, lqEndM);
    return {
      periodLabel: `Q${lastQ + 1} ${lqY}`,
      rangeLabel: `${MONTH_NAMES[lqStartM]} 1 – ${MONTH_NAMES[lqEndM]} ${lqEndDay}, ${lqY}`,
      retentionSectionLabel: "Last Quarter",
      partialMonthWarning: undefined,
    };
  }
  return {
    periodLabel: `${y} YTD`,
    rangeLabel: `Jan 1 – ${MONTH_NAMES[m]} ${d}, ${y}`,
    retentionSectionLabel: "This Year",
    partialMonthWarning: undefined,
  };
}
